import { showErrorAlert } from 'actions';
import { changeEndDate, changeLengthOfWork, changeStartDate } from 'actions/import';
import DateRangePicker from 'components/Calendar';
import SelectRedesign from 'components/Select/SelectRedesign';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getImportEndDate,
  getImportLengthOfWork,
  getImportStartDate,
  getImportType,
} from 'selectors/import';
import { useSelector } from 'store';

export const lenghtOfWorkOptions = [
  {
    value: '',
    label: 'Length of work',
  },
  {
    value: '7',
    label: '7 days',
  },
  {
    value: '30',
    label: '30 days',
  },
  {
    value: '60',
    label: '60 days',
  },
  {
    value: '90',
    label: '90 days',
  },
  {
    value: '180',
    label: '180 days',
  },
  {
    value: '365',
    label: '365 days',
  },
];

const ImportDateSelection: React.FC = () => {
  const dispatch = useDispatch();

  const importType = useSelector(getImportType);

  const startDate = useSelector(getImportStartDate);
  const endDate = useSelector(getImportEndDate);
  const lengthOfWork = useSelector(getImportLengthOfWork);

  const [startInputDate, setStartInputDate] = useState(
    startDate ? moment(startDate).format('MM/DD/yyyy') : ''
  );

  const [endInputDate, setEndInputDate] = useState(
    endDate ? moment(endDate).format('MM/DD/yyyy') : ''
  );

  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  const startDateRef = useRef<HTMLDivElement>(null);
  const endDateRef = useRef<HTMLDivElement>(null);

  const getEndDate = (value: string): string => {
    if (!startDate) return '';

    const trueEndDate = moment(startDate).add(value, 'days').format('MM/DD/YYYY');

    return trueEndDate;
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setStartInputDate(value);

    const startValueDate = moment(value, 'MM/DD/YYYY', true);

    if (endDate && moment(endDate).isValid()) {
      const endValueDate = moment(endDate);
      if (startValueDate.isAfter(endValueDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (startValueDate.isValid()) {
      dispatch(changeStartDate(startValueDate.toString()));
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      dispatch(changeStartDate(''));
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setEndInputDate(value);

    const endValueDate = moment(value, 'MM/DD/YYYY', true);

    if (startDate && moment(startDate).isValid()) {
      const startValueDate = moment(startDate);
      if (endValueDate.isBefore(startValueDate)) {
        dispatch(showErrorAlert('Invalid Date Range'));
        return;
      }
    }

    if (endValueDate.isValid()) {
      dispatch(changeEndDate(endValueDate.toString()));
    } else if (value.length > 9) {
      dispatch(showErrorAlert('Invalid Date'));
    } else if (value.length === 0) {
      dispatch(changeEndDate(''));
    }
  };

  const handleLengthOfWorkChange = (value: string): void => {
    dispatch(changeLengthOfWork(value));

    const date = getEndDate(value);

    dispatch(changeEndDate(date));
    setEndInputDate(moment(date).format('MM/DD/YYYY'));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex flex-row gap-1">
          <span>Start Date</span>
          <span className="litlingo-red-color">*</span>
        </div>
        <div
          aria-hidden
          ref={startDateRef}
          className="w-43 flex flex-row gap-4 items-end"
          onClick={(): void => setShowStartCalendar(true)}
        >
          <div className="w-full h-8 flex flex-row justify-start gap-1 px-1 py-1 text-body bg-white border border-litlingo-gray-2 rounded">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.5 2.75C5.5 2.33579 5.16421 2 4.75 2C4.33579 2 4 2.33579 4 2.75V4H3C1.89543 4 1 4.89543 1 6V17C1 18.1046 1.89543 19 3 19H17C18.1046 19 19 18.1046 19 17V6C19 4.89543 18.1046 4 17 4H16.5V2.75C16.5 2.33579 16.1642 2 15.75 2C15.3358 2 15 2.33579 15 2.75V4H5.5V2.75ZM17.5 8H2.5V17C2.5 17.2761 2.72386 17.5 3 17.5H17C17.2761 17.5 17.5 17.2761 17.5 17V8Z"
                fill="#5C5C5C"
              />
            </svg>

            <div className="w-full flex text-body flex-row gap-1">
              <input
                data-testid="date-filter-input"
                type="text"
                placeholder="MM/DD/YYYY"
                className="bg-transparent placeholder-litlingo-gray-3 w-22"
                value={startInputDate}
                onChange={handleStartDateChange}
              />
            </div>
          </div>

          {showStartCalendar && (
            <DateRangePicker
              containerRef={startDateRef}
              defaultRange={{
                from: startDate ? moment(startDate).toDate() : undefined,
                to: undefined,
              }}
              setShowCalendar={(): void => {
                setShowStartCalendar(!showStartCalendar);
              }}
              setDateRange={(range): void => {
                if (range.from) {
                  dispatch(changeStartDate(range.from.toString()));
                  setStartInputDate(moment(range.from).format('MM/DD/YYYY'));
                  setShowStartCalendar(!showStartCalendar);
                }
              }}
              dateOption="after"
              disableFutureDates={false}
            />
          )}
        </div>
      </div>
      {importType === 'lookback' && (
        <div className="flex flex-col gap-1">
          <div className="flex flex-row gap-1">
            <span>End Date</span>
            <span className="litlingo-red-color">*</span>
          </div>

          <div className="flex flex-row gap-2 items-center">
            <div
              aria-hidden
              ref={endDateRef}
              className="w-43 flex flex-row gap-4 items-end"
              onClick={(): void => setShowEndCalendar(true)}
            >
              <div className="w-full h-8 flex flex-row justify-start gap-1 px-1 py-1 text-body bg-white border border-litlingo-gray-2 rounded">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M5.5 2.75C5.5 2.33579 5.16421 2 4.75 2C4.33579 2 4 2.33579 4 2.75V4H3C1.89543 4 1 4.89543 1 6V17C1 18.1046 1.89543 19 3 19H17C18.1046 19 19 18.1046 19 17V6C19 4.89543 18.1046 4 17 4H16.5V2.75C16.5 2.33579 16.1642 2 15.75 2C15.3358 2 15 2.33579 15 2.75V4H5.5V2.75ZM17.5 8H2.5V17C2.5 17.2761 2.72386 17.5 3 17.5H17C17.2761 17.5 17.5 17.2761 17.5 17V8Z"
                    fill="#5C5C5C"
                  />
                </svg>

                <div className="w-full flex text-body flex-row gap-1">
                  <input
                    data-testid="date-filter-input"
                    type="text"
                    placeholder="MM/DD/YYYY"
                    className="bg-transparent placeholder-litlingo-gray-3 w-22"
                    value={endInputDate}
                    onChange={handleEndDateChange}
                  />
                </div>
              </div>

              {showEndCalendar && (
                <DateRangePicker
                  containerRef={endDateRef}
                  defaultRange={{
                    from: endDate ? moment(endDate).toDate() : undefined,
                    to: undefined,
                  }}
                  setShowCalendar={(): void => {
                    setShowEndCalendar(!showEndCalendar);
                  }}
                  setDateRange={(range): void => {
                    if (range.from) {
                      dispatch(changeEndDate(range.from.toString()));
                      setEndInputDate(moment(range.from).format('MM/DD/YYYY'));
                    }
                  }}
                  dateOption="after"
                  disableFutureDates={false}
                />
              )}
            </div>

            <span className="font-bold">OR</span>

            <div className="flex-shrink-0 w-43">
              <SelectRedesign
                onChange={(value: string): void => handleLengthOfWorkChange(value)}
                options={lenghtOfWorkOptions}
                value={lengthOfWork}
                placeholder="Length of work"
                dataTestid="lenght-of-work-select"
                disabled={!startDate}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImportDateSelection;
